import { useState, useEffect, useMemo } from "react";
import "./Competitionplayer.css";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import backArrow from "../../../../images/arrow.png";
import whiteArrow from "../../../../images/white-arrow.png";
import Skeleton from "../../../../components/Skeleton";
import worldCup from "../../../home/HomeImage/worldcup.png";
import { Helmet } from "react-helmet-async";

const Competitionplayer = ({
  dark,
  players,
  competition,
  competitions,
  league,
  MyBaseUrl,
  playersLoading,
  playersError,
  active,
  handleNavClick,
}) => {
  const navigate = useNavigate();
  const { name, id } = useParams();

  const [viewState, setViewState] = useState({});
  const [leagueDataFromApi, setLeagueDataFromApi] = useState(null);

  const createSlug = (leagueName) => {
    return leagueName
      ?.toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  useEffect(() => {
    const fetchLeagueInfo = async () => {
      try {
        const response = await fetch(`${MyBaseUrl}/competitions`);

        if (!response.ok) {
          throw new Error("Failed to fetch competitions list");
        }

        const data = await response.json();
        const competitions = data?.data || [];

        const foundCompetition = competitions.find(
          (comp) => String(comp?.league?.id) === id,
        );

        if (foundCompetition) {
          setLeagueDataFromApi(foundCompetition?.league);
        }
      } catch (error) {
        console.error("Failed to fetch league info:", error);
      }
    };

    if (id) {
      fetchLeagueInfo();
    }
  }, [id]);

  const toggleView = (key) => {
    setViewState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const safePlayers = Array.isArray(players) ? players : [];

  const firstStats = safePlayers?.[0]?.statistics?.[0];
  const leagueDataFromPlayers = firstStats?.league;

  const leagueData = leagueDataFromPlayers || leagueDataFromApi;

  const leagueLogo = leagueData?.logo;
  const leagueName = leagueData?.name;
  const leagueCountry = leagueData?.country;
  const leagueSeason = leagueData?.season;

  const currentCompetition = useMemo(() => {
    if (competition) return competition;
    if (!competitions || !id) return null;

    return competitions.find((comp) => {
      return String(comp?.league?.id) === id;
    });
  }, [competition, competitions, id]);

  const [isStickyTop, setIsStickyTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth <= 600) {
        if (window.scrollY >= 98) {
          setIsStickyTop(true);
        } else {
          setIsStickyTop(false);
        }
      } else {
        if (window.scrollY >= 300) {
          setIsStickyTop(true);
        } else {
          setIsStickyTop(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!currentCompetition) {
    return <Skeleton dark={dark} />;
  }

  const leagueDataSafe = currentCompetition?.league || {};
  const leagueId = leagueDataSafe?.id;
  const slug = createSlug(leagueDataSafe?.name);

  const statCategories = [
    {
      key: "goals",
      title: "Top Goal Scorers",
      getValue: (stats) => stats?.goals?.total || 0,
      label: (value) => `${value} goals`,
    },
    {
      key: "fouls",
      title: "Top Fouls",
      getValue: (stats) => stats?.fouls?.committed || 0,
      label: (value) => `${value} fouls`,
    },
    {
      key: "shots",
      title: "Top Shots on Target",
      getValue: (stats) => stats?.shots?.on || 0,
      label: (value) => `${value} shots on target`,
    },
    {
      key: "yellow",
      title: "Yellow Cards",
      getValue: (stats) => stats?.cards?.yellow || 0,
      label: (value) => `${value} yellow cards`,
    },
    {
      key: "red",
      title: "Red Cards",
      getValue: (stats) => stats?.cards?.red || 0,
      label: (value) => `${value} red cards`,
    },
  ];

  return (
    <div className="homer">
      <Helmet>
        <title>{`Top Players | ${leagueDataSafe?.name || "Competition"}`}</title>

        <meta
          name="description"
          content={`Explore top performing players in ${leagueDataSafe?.name}. View goals, cards, shots, and season statistics.`}
        />

        <meta
          name="keywords"
          content="top players, football stats, goals, assists, yellow cards, red cards, player rankings"
        />

        <meta property="og:title" content="Top Players Rankings" />
        <meta
          property="og:description"
          content={`See top goal scorers and best performing players in ${leagueDataSafe?.name}.`}
        />
      </Helmet>

      <div className={`playerWrapper`}>
        {safePlayers.length === 0 ? (
          <Skeleton dark={dark} count={6} />
        ) : safePlayers.length <= 0 ? (
          <></>
        ) : (
          <section
            className="overall"
            style={{ backgroundColor: "transparent" }}>
            {statCategories.map((category) => {
              const sortedPlayers = [...safePlayers].sort((a, b) => {
                const statA = category.getValue(a?.statistics?.[0]);
                const statB = category.getValue(b?.statistics?.[0]);
                return statB - statA;
              });

              const isExpanded = viewState[category.key];

              return (
                <section className="firstsection" key={category.key}>
                  <div className="playhead">
                    <h1
                      style={
                        dark ? { color: "#fafafaff" } : { color: "#161819" }
                      }>
                      {category.title}
                    </h1>

                    <button
                      onClick={() => toggleView(category.key)}
                      style={
                        dark
                          ? { color: "#cfceceff", marginTop: "10px" }
                          : { color: "#161819", marginTop: "10px" }
                      }>
                      {isExpanded ? "View less" : "View all"}
                    </button>
                  </div>

                  <div className="highlight">
                    <div className={isExpanded ? "grid" : "slide1"}>
                      {sortedPlayers.map((item, index) => {
                        const player = item?.player;
                        const stats = item?.statistics?.[0];
                        const team = stats?.team;
                        const statValue = category.getValue(stats);

                        if (!player?.id) return null;

                        return (
                          <NavLink
                            to={`/players/${player.name
                              ?.trim()
                              .replace(/[^a-zA-Z.\s]/g, "")
                              .replace(/\s+/g, "-")
                              .toLowerCase()}-${player.id}`}
                            state={{ player, leagueSlug: slug }}
                            key={player.id || index}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              padding: "0",
                            }}>
                            <div
                              className="imgdiv1"
                              style={
                                dark
                                  ? {
                                      backgroundColor: "#1a1a1a",
                                      border: "1px solid #333",
                                    }
                                  : {}
                              }>
                              <img
                                src={player?.photo}
                                alt={player?.name}
                                className="behind"
                              />

                              <p>
                                <img
                                  src={team?.logo}
                                  alt={team?.name}
                                  className="mylogos"
                                />
                                <strong>
                                  <b>{player?.firstname}</b>{" "}
                                  {player?.lastname?.length <= 9 && (
                                    <b>{player.lastname}</b>
                                  )}
                                </strong>
                                <button>{category.label(statValue)}</button>
                              </p>
                            </div>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
};

export default Competitionplayer;
